import { nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

export enum EndpointAdServerType {
  Exchange = 'EXCHANGE',
  FreeWheel = 'FREE_WHEEL',
  Gam = 'GAM',
  Publica = 'PUBLICA',
  Roku = 'ROKU',
  SpringServe = 'SPRING_SERVE',
}

export enum EndpointIntegrationType {
  Ortb = 'ORTB',
  Vast = 'VAST',
}

export enum EndpointRegion {
  Us = 'use',
  Eu = 'euc',
}

export enum EndpointProjection {
  All = 'ALL',
  Optimum = 'OPTIMUM',
  Minimum = 'MIN',
}

export enum EndpointLookupStatus {
  Active = 'ACTIVE',
}

export enum EndpointPlacementType {
  Standard = 'STANDARD',
}

const deliveryTypeSchema = nullableOptional(z.string());

export const endpointLookupPageSchema = z.looseObject({
  content: z.array(
    z.looseObject({
      id: z.coerce.string(),
      name: nullableOptional(z.string()),
      secondaryId: nullableOptional(z.coerce.string()),
      deliveryType: deliveryTypeSchema,
    }),
  ),
  totalElements: z.number().int().nonnegative().optional(),
});

export const endpointMacroSchema = z.looseObject({
  name: nullableOptional(z.string()),
  value: z.string(),
  description: nullableOptional(z.string()),
});

export const endpointMacrosSchema = z.array(endpointMacroSchema);
export const generatedEndpointSchema = z.string();

export type EndpointLookupOption = z.infer<typeof endpointLookupPageSchema>['content'][number];
export type EndpointMacro = z.infer<typeof endpointMacroSchema>;

export interface EndpointGeneratorRequest {
  pid: string;
  placementId: string;
  categoryType?: string;
  adServerType: EndpointAdServerType;
  region: EndpointRegion;
  audio: boolean;
}

const minimumParameters = [
  'network_id',
  'publisher_id',
  'placement_id',
  'domain',
  'page_url',
  'referer_url',
  'app_bundle_id',
  'app_store_url',
  'ip',
  'ua',
  'ifa',
  'ifa_type',
  'height',
  'width',
  'audio',
] as const;

const optimumParameters = [...minimumParameters, 'min_duration', 'max_duration'] as const;

export function projectGeneratedEndpoint(endpoint: string, projection: EndpointProjection) {
  if (projection === EndpointProjection.All) return endpoint;

  const queryIndex = endpoint.indexOf('?');
  if (queryIndex < 0) return endpoint;

  const base = endpoint.slice(0, queryIndex);
  const query = endpoint.slice(queryIndex + 1);
  const order = projection === EndpointProjection.Minimum ? minimumParameters : optimumParameters;
  const orderByName = new Map<string, number>(order.map((name, index) => [name, index]));
  const parameters = query
    .split('&')
    .filter(Boolean)
    .filter((pair) => orderByName.has(parameterName(pair)))
    .sort(
      (left, right) =>
        (orderByName.get(parameterName(left)) ?? 0) - (orderByName.get(parameterName(right)) ?? 0),
    );

  return parameters.length ? `${base}?${parameters.join('&')}` : base;
}

export function endpointLookupLabel(option: EndpointLookupOption) {
  return option.name?.trim() || option.secondaryId || option.id;
}

function parameterName(pair: string) {
  const separator = pair.indexOf('=');
  return separator < 0 ? pair : pair.slice(0, separator);
}
